/**
 * OAuth Store for Staff Interface
 *
 * Uses the shared OAuth store factory from packages/shared.
 * Configured with SI-specific settings including:
 * - Cross-tab synchronization for multi-tab staff workflows
 * - Maintenance mode detection and handling
 */

import { createOAuthStore } from '@shared/core/auth';

export const OAUTH_STORE_ID = 'store-oauth';

/**
 * Clear every per-staff Pinia store on session teardown. Mirrors
 * apps/ui/src/stores/oauth.ts clearUserScopedState(). Called from the
 * shared onLogout hook below (deliberate logout + cross-tab broadcast)
 * AND from boot/api.ts handleAuthFailure() (the 401 teardown path, which
 * clears tokens directly and never runs onLogout).
 *
 * Clearing userStore.data matters most: without it the next sign-in
 * reuses the previous staff member's UUID/profile from localStorage and
 * the websocket boot file subscribes to `user.<old-uuid>` channels
 * (permanent 403). The rest prevents the previous staff member's
 * tickets/chat queue/notifications flashing after a same-tab re-login.
 *
 * All imports are lazy to avoid circular dependencies at module load
 * time (these stores reach boot/api.ts, which imports this module).
 */
export function clearUserScopedState(): void {
  void import('src/stores/user').then(({ useUserStore }) => {
    useUserStore().clearData();
  });
  void import('src/stores/notifications').then(({ useNotificationStore }) => {
    useNotificationStore().reset();
  });
  void import('src/stores/tickets').then(({ useTicketsStore }) => {
    useTicketsStore().reset();
  });
  void import('src/stores/supportChat').then(({ useSupportChatStore }) => {
    useSupportChatStore().reset();
  });
  void import('src/stores/acquisition').then(({ useAcquisitionStore }) => {
    useAcquisitionStore().reset();
  });
  void import('src/stores/marketingTemplates').then(({ useMarketingTemplatesStore }) => {
    useMarketingTemplatesStore().reset();
  });
  void import('src/stores/features').then(({ useFeaturesStore }) => {
    useFeaturesStore().clearFeatures();
  });
}

/**
 * Create the OAuth store using the shared factory.
 *
 * Reads VITE_* env directly (not via `boot/config`) so the values are
 * compile-time substituted to literals at build time. The previous
 * `config.api.baseUrl` runtime lookup tripped an ESM cycle in the
 * Vite 8 / rolldown chunked production build — `config` resolved to
 * undefined when this top-level factory call ran, throwing
 * `Cannot read properties of undefined (reading 'api')` on cloud SI.
 * Mirrors the UI pattern in apps/ui/src/stores/oauth.ts and the same
 * shape b7ceeb9a fixed for the axios cycle in oauth-store.ts.
 */
export const useOAuthStore = createOAuthStore({
  apiUrl: import.meta.env.VITE_API_URL || '',
  clientId: import.meta.env.VITE_OAUTH_CLIENT_ID || '',
  // clientSecret intentionally not set — the API holds the confidential staff
  // client secret server-side, so it is never bundled into the SI build.
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 50000,
  storeId: OAUTH_STORE_ID,

  // App identifier for session conflict detection
  // This prevents simultaneous sessions in UI and SI
  appId: 'si',

  isNativeApp: false, // SI is web-only

  // Cross-tab synchronization configuration
  // Enables distributed refresh lock and logout sync across browser tabs
  crossTabSync: {
    channelName: 'emalino-si-auth', // Unique channel for SI (separate from UI)
    enabled: true,
  },

  // Handle logout (clear app-specific state). Runs on deliberate logout in
  // this tab AND in every passive tab receiving the cross-tab broadcast.
  onLogout: () => {
    clearUserScopedState();

    // The router guard only runs on navigation, so a passive tab sitting on
    // a protected page would keep rendering (now-cleared) staff state until
    // the user interacts. Bounce it to the login page explicitly.
    void import('src/boot/api').then(({ getAppRouter }) => {
      const router = getAppRouter();
      if (router && router.currentRoute.value.name !== 'login') {
        void router.replace({ name: 'login' });
      }
    });
  },

  // Handle maintenance mode (503 responses)
  onMaintenanceMode: (isActive, message, retryAfter) => {
    // Lazy import to avoid circular dependency at module load time
    void import('src/stores/app').then(({ useAppStore }) => {
      const appStore = useAppStore();
      appStore.setMaintenanceMode(isActive, message, retryAfter);
    });
  },
});

// Re-export types for convenience
export type {
  OAuthTokenData,
  OAuthState,
  OAuthErrorType,
  AuthSyncMessage,
  CrossTabSyncConfig,
  MaintenanceModeState,
} from '@shared/core/auth';
